/**
 * Settings > Project > Datasets — export access.
 *
 * The console (models.trodo.ai) and scripts pull a dataset's labelled JSONL
 * with a scoped export token: one team, optionally a fixed list of datasets,
 * read-only. The secret is shown once; the list shows a prefix. Every pull
 * is audited and the last ones are listed here.
 *
 * Gating mirrors the server (backend/routes/datasetsRouter.js): listing needs
 * team membership; minting and revoking need INTEGRATIONS_DEVELOPER_MANAGE,
 * the same bar as MCP keys.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Trash2 } from 'lucide-react';

import { useAppDialog } from 'shared/context/AppDialogContext';
import { usePermissions } from 'shared/hooks/usePermissions';
import { PERMISSIONS } from 'shared/constants/permissions';
import {
  settingsInputClass, settingsSolidButtonClass, settingsIconButtonClass, settingsTableShellClass, settingsModalClass,
} from 'features/settings/components/settingsTheme';
import { BrandLoaderInline } from 'features/settings/components/settingsLoader';
import {
  tableHeaderRowClass, tableHeaderCellClass, tableRowClass, tableDataCellClass, activityNeutralButtonClass,
} from 'features/activity/styles/dataTableStyles';
import BrandMultiSelect from 'features/playground/components/BrandMultiSelect';
import {
  listExportTokens, createExportToken, revokeExportToken, listExportAudit, listDatasets,
} from 'features/datasets/api/datasetsApi';

const DOCS_URL = 'https://docs.trodo.ai/datasets/export';
const EXPORT_URL = 'https://sdkapi.trodo.ai/api/sdk/datasets/<dataset id or name>/export?rows=expected';

function humanError(e) {
  return e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Request failed';
}

function CopyButton({ text, ariaLabel }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" aria-label={ariaLabel || 'Copy'} title={copied ? 'Copied' : 'Copy'} className={settingsIconButtonClass}
      onClick={async () => { try { await navigator.clipboard.writeText(text); } catch { /* blocked */ } setCopied(true); setTimeout(() => setCopied(false), 1800); }}>
      {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
    </button>
  );
}

function CreateTokenModal({ onClose, onCreated }) {
  const [label, setLabel] = useState('');
  const [datasets, setDatasets] = useState([]);
  const [scope, setScope] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { listDatasets().then((d) => setDatasets(d || [])).catch(() => setDatasets([])); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    setError(null); setCreating(true);
    try {
      onCreated(await createExportToken({ label: label.trim(), datasetIds: scope.length ? scope : null }));
    } catch (er) { setError(humanError(er)); } finally { setCreating(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" style={{ background: 'var(--surface-overlay)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="dx-create-title" className={`${settingsModalClass} max-w-md p-5`} onMouseDown={(e) => e.stopPropagation()}>
        <h2 id="dx-create-title" className="mb-3 text-[14px] font-semibold text-[color:var(--text-primary)]">Create export token</h2>
        <label htmlFor="dx-label" className="mb-1 block text-[12px] text-[color:var(--text-muted)]">Label</label>
        <input id="dx-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. models.trodo.ai" required className={settingsInputClass} />
        <div className="mt-4">
          <div className="mb-1 text-[12px] text-[color:var(--text-muted)]">Datasets</div>
          <BrandMultiSelect value={scope} options={datasets.map((d) => ({ value: d.id, label: d.name }))} onChange={setScope} placeholder="Every dataset of this project" minWidth="100%" />
          <p className="mt-1 text-[11px] text-[color:var(--text-muted)]">Leave empty for every dataset, now and later. Pick some to pin the token to them.</p>
        </div>
        {error && <p role="alert" className="mt-3 text-[12px] text-[color:var(--feedback-error)]">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={activityNeutralButtonClass}>Cancel</button>
          <button type="submit" disabled={creating || !label.trim()} className={settingsSolidButtonClass}>{creating ? 'Creating…' : 'Create token'}</button>
        </div>
      </form>
    </div>
  );
}

const ago = (d) => {
  if (!d) return 'Never';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(d)) / 60000));
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};
const kb = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`);

export default function DatasetsExportPanel() {
  const dialog = useAppDialog();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.INTEGRATIONS_DEVELOPER_MANAGE);

  const [tokens, setTokens] = useState([]);
  const [pulls, setPulls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [justCreated, setJustCreated] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([listExportTokens(), listExportAudit({ limit: 50 })]);
      setTokens(t); setPulls(p); setError(null);
    } catch (e) { setError(humanError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  async function revoke(t) {
    const ok = await dialog.confirm(`Anything still pulling with "${t.label}" will get 401 from now on. This cannot be undone.`,
      { title: 'Revoke this token?', confirmLabel: 'Revoke', cancelLabel: 'Cancel', danger: true });
    if (!ok) return;
    try { await revokeExportToken(t.id); await refresh(); } catch (e) { setError(humanError(e)); }
  }

  const active = tokens.filter((t) => !t.revoked_at);
  const colSpan = canManage ? 5 : 4;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 text-[12px] text-[color:var(--text-muted)]">
          <p>A token reads a dataset as JSONL: input, output, expected, evaluations, source. Read-only, one project, optionally pinned to some datasets.</p>
          <div className="mt-1 flex min-w-0 items-center gap-1">
            <code className="truncate font-mono text-[11.5px] text-[color:var(--text-primary)]">GET {EXPORT_URL}</code>
            <CopyButton text={`curl -H "Authorization: Bearer trodo_dx_…" "${EXPORT_URL}"`} ariaLabel="Copy an example request" />
            <a href={DOCS_URL} target="_blank" rel="noopener noreferrer" className="ml-1 underline-offset-2 hover:text-[color:var(--text-primary)] hover:underline">Docs</a>
          </div>
        </div>
        {canManage && !showCreate && !justCreated ? (
          <button type="button" onClick={() => setShowCreate(true)} className={settingsSolidButtonClass}>Create token</button>
        ) : null}
      </div>

      {!canManage ? <p className="text-[12px] text-[color:var(--text-muted)]">Only owners, admins and developers can create or revoke tokens.</p> : null}
      {error ? <p role="alert" className="text-[12px] text-[color:var(--feedback-error)]">{error}</p> : null}

      {justCreated ? (
        <div className={`${settingsTableShellClass} p-3`}>
          <p className="text-[12px] font-semibold text-[color:var(--text-primary)]">Copy this token now. It won't be shown again.</p>
          <div className="mt-2 flex items-center gap-1">
            <code className="min-w-0 flex-1 break-all font-mono text-[12px] text-[color:var(--text-primary)]">{justCreated.token}</code>
            <CopyButton text={justCreated.token} ariaLabel="Copy the new export token" />
          </div>
          <div className="mt-3"><button type="button" onClick={() => { setJustCreated(null); refresh(); }} className={activityNeutralButtonClass}>Done</button></div>
        </div>
      ) : null}

      {loading && tokens.length === 0 ? <BrandLoaderInline label="Loading tokens" /> : (
        <div className={settingsTableShellClass}>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <caption className="sr-only">Export tokens</caption>
              <thead>
                <tr className={tableHeaderRowClass}>
                  <th scope="col" className={tableHeaderCellClass}>Label</th>
                  <th scope="col" className={tableHeaderCellClass}>Token</th>
                  <th scope="col" className={tableHeaderCellClass}>Datasets</th>
                  <th scope="col" className={tableHeaderCellClass}>Last used</th>
                  {canManage ? <th scope="col" className="h-8 w-1 whitespace-nowrap px-3"><span className="sr-only">Actions</span></th> : null}
                </tr>
              </thead>
              <tbody>
                {!loading && active.length === 0 ? (
                  <tr><td colSpan={colSpan} className="px-3.5 py-8 text-center text-[12px] text-[color:var(--text-muted)]">No tokens yet</td></tr>
                ) : null}
                {active.map((t) => (
                  <tr key={t.id} className={`${tableRowClass} group !cursor-default`}>
                    <td className={tableDataCellClass}>{t.label}</td>
                    <td className={`${tableDataCellClass} font-mono text-[11px] text-[color:var(--text-muted)]`}>{t.token_prefix ? `${t.token_prefix}…` : '—'}</td>
                    <td className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>{Array.isArray(t.dataset_ids) && t.dataset_ids.length ? `${t.dataset_ids.length} pinned` : 'All'}</td>
                    <td className={`${tableDataCellClass} whitespace-nowrap text-[color:var(--text-muted)]`}>{ago(t.last_used_at)}</td>
                    {canManage ? (
                      <td className="w-1 whitespace-nowrap px-3 text-right">
                        <button type="button" onClick={() => revoke(t)} aria-label={`Revoke ${t.label}`} title="Revoke"
                          className={`${settingsIconButtonClass} opacity-0 group-hover:opacity-100 focus-visible:opacity-100`}><Trash2 size={13} aria-hidden /></button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h3 className="mb-2 text-[12px] font-semibold text-[color:var(--text-primary)]">Recent pulls</h3>
        <div className={settingsTableShellClass}>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <caption className="sr-only">Export audit</caption>
              <thead>
                <tr className={tableHeaderRowClass}>
                  <th scope="col" className={tableHeaderCellClass}>When</th>
                  <th scope="col" className={tableHeaderCellClass}>Dataset</th>
                  <th scope="col" className={tableHeaderCellClass}>Who</th>
                  <th scope="col" className={tableHeaderCellClass}>Labels</th>
                  <th scope="col" className={tableHeaderCellClass}>Rows</th>
                  <th scope="col" className={tableHeaderCellClass}>Size</th>
                </tr>
              </thead>
              <tbody>
                {!loading && pulls.length === 0 ? (
                  <tr><td colSpan={6} className="px-3.5 py-8 text-center text-[12px] text-[color:var(--text-muted)]">Nothing has been exported yet</td></tr>
                ) : null}
                {pulls.map((p) => (
                  <tr key={p.id} className={`${tableRowClass} !cursor-default`} data-testid="pull-row">
                    <td className={`${tableDataCellClass} whitespace-nowrap text-[color:var(--text-muted)]`}>{ago(p.created_at)}</td>
                    <td className={tableDataCellClass}>{p.dataset_name || p.dataset_id}{p.dataset_version != null ? <span className="ml-1 text-[color:var(--text-muted)]">v{p.dataset_version}</span> : null}</td>
                    <td className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>{p.token_id ? `token ${p.token_label || p.token_prefix || ''}`.trim() : 'a signed-in member'}</td>
                    <td className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>{p.rows_filter === 'expected' ? 'with Expected' : p.rows_filter || 'all'}</td>
                    <td className={`${tableDataCellClass} tabular-nums text-[color:var(--text-muted)]`}>{Number(p.row_count).toLocaleString()}</td>
                    <td className={`${tableDataCellClass} tabular-nums text-[color:var(--text-muted)]`}>{kb(Number(p.bytes) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCreate ? <CreateTokenModal onClose={() => setShowCreate(false)} onCreated={(t) => { setJustCreated(t); setShowCreate(false); }} /> : null}
    </div>
  );
}
